import { ApiClient } from "../api-client";
import { ApiClientError, handleApiErrorWithSchema } from "../errors";
import { TypeFromSchemaValidator } from "../schema-validator";
import LoggerClient from "./logger";
import schemaValidators from "./schema-validators";
import lmsInfoSchemaValidators from "../lms-info/schema-validators";
import stateClient from "./state";

export default class ProctoringSessionClient extends ApiClient<typeof schemaValidators> {
	readonly logger: LoggerClient;
	readonly stateClient: stateClient;

	constructor(config: ApiClient["config"]) {
		super(config, schemaValidators);
		this.logger = new LoggerClient(config);
		this.stateClient = new stateClient(config);
	}

	startProctoring = async (
		assessmentInfo: TypeFromSchemaValidator<typeof lmsInfoSchemaValidators.assessmentInfo>,
		devicesInfo?: {
			os?: string;
			chrome?: string;
			extension?: string;
			userAgent?: string;
		},
		capabilities?: {
			logEndUpload?: boolean;
		},
	): Promise<string> => {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/verity/quiz/event/${assessmentInfo.lms}/${assessmentInfo.hostname}/${assessmentInfo.courseId}/${assessmentInfo.quizId}`,
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						type: "session",
						name: "start proctoring",
						time: new Date(),
						details: { success: true, devicesInfo: devicesInfo, capabilities },
					}),
					credentials: "include",
				},
			);
			if (response.ok) {
				const resObj: unknown = await response.json();
				if (this.schemaValidators.startProctoring.validate(resObj)) {
					return resObj.sessionId;
				}
			}
			throw new ApiClientError(new Error(`Invalid Response: ${response.status} - ${response.statusText}`));
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	upload = async (
		recordingBlobInfo: TypeFromSchemaValidator<(typeof schemaValidators)["recordingBlobInfo"]>,
		blob: Blob,
	) => {
		const searchParams = new URLSearchParams({
			segment: `${recordingBlobInfo.blobNumber}`,
			url: "upload",
		});
		const response = await super.fetch(
			`${this.config.verityOrigin}/api/verity/quiz/recording/${recordingBlobInfo.proctoringSessionId}/${
				recordingBlobInfo.recordingType === "display" ? "screencapture" : recordingBlobInfo.recordingType
			}/${recordingBlobInfo.recordingNumber}?${searchParams.toString()}`,
			{
				credentials: "include",
			},
		);
		if (!response.ok) {
			throw new ApiClientError(
				new Error(`Upload to get S3 pre-signed URL: ${response.status} - ${response.statusText}`),
			);
		}
		const uploadUrl: unknown = await response.json();

		if (schemaValidators.uploadS3Url.validate(uploadUrl)) {
			const s3Response = await super.fetch(uploadUrl.url, {
				method: "PUT",
				body: blob,
			});
			if (!s3Response.ok) {
				throw new ApiClientError(new Error(`Upload to S3 failed: ${s3Response.status} - ${s3Response.statusText}`));
			}
		}
	};

	uploadRoomScan = async (proctoringSessionId: string, blob: Blob) => {
		const response = await super.fetch(
			`${this.config.verityOrigin}/api/verity/quiz/recording/${proctoringSessionId}/room_scan.webm`,
			{
				method: "PUT",
				body: blob,
			},
		);
		if (!response.ok) {
			throw new ApiClientError(new Error(`Upload to S3 failed: ${response.status} - ${response.statusText}`));
		}
	};

	getRoomScanUrl = async (proctoringSessionId: string) => {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/verity/quiz/recording/${proctoringSessionId}/room_scan/latest.webm`,
				{
					method: "GET",
				},
			);
			if (!response.ok) {
				throw new ApiClientError(
					new Error(`Failed to retrieve room scan video: ${response.status} - ${response.statusText}`),
				);
			}
			const videoUrl = response.url;
			return videoUrl;
		} catch (error) {
			console.error("Error fetching room scan video: ", error);
			throw error;
		}
	};

	getCachedRoomScanUrl = async (proctoringSessionId: string) => {
		try {
			const urlResponse = await super.fetch(
				`${this.config.verityOrigin}/api/verity/quiz/recording/${proctoringSessionId}/room_scan/url`,
			);
			if (urlResponse.ok) {
				const resObj: unknown = await urlResponse.json();
				if (this.schemaValidators.url.validate(resObj)) {
					const videoResponse = await super.fetch(resObj.url, { method: "HEAD" });
					if (videoResponse.ok) {
						return resObj.url;
					}
				}
			}
		} catch (error) {
			console.error("Error fetching room scan video: ", error);
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		return undefined;
	};

	getIdPhotoUrl = async (proctoringSessionId: string, type: "id" | "person") => {
		const ext = "png";
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/verity/photo/${proctoringSessionId}-${type}.${ext}`,
			);
			if (response.ok) {
				const imageBlob = await response.blob();
				return URL.createObjectURL(imageBlob);
			}
		} catch (error) {
			console.error("Error fetching ID photo: ", error);
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		return undefined;
	};

	getLmsContext = async (sessionId: string) => {
		try {
			const res = await super.fetch(
				`${this.config.verityOrigin}/api/verity/proctoring-session/${sessionId}/lms-context`,
			);
			if (res.ok) {
				const lmsContext: unknown = await res.json();
				if (this.schemaValidators.lmsContext.validate(lmsContext)) {
					return lmsContext;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	async isCorrectProctoringSessionId(
		sessionId: string,
		assessmentInfo: TypeFromSchemaValidator<typeof lmsInfoSchemaValidators.assessmentInfo>,
	) {
		try {
			const lmsContext = await this.getLmsContext(sessionId);
			return assessmentInfo.quizId === lmsContext.assessment.quizId;
		} catch (err) {
			console.error(err);
		}
		return false;
	}

	async getSessionResultList(lmsQuizId: string, courseId: number) {
		try {
			const res = await super.fetch(
				`${this.config.verityOrigin}/api/verity/test-results/${courseId}/${lmsQuizId}?session=all`,
				{
					credentials: "include",
				},
			);
			if (res.ok) {
				const events: unknown = await res.json();
				if (this.schemaValidators.testResults.validate(events)) {
					return events;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	}

	async getSessionList(courseId: number) {
		try {
			const res = await super.fetch(`${this.config.verityOrigin}/api/verity/test-results/${courseId}?session=all`, {
				credentials: "include",
			});
			if (res.ok) {
				const events: unknown = await res.json();
				if (this.schemaValidators.testResults.validate(events)) {
					return events;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	}

	async getSessionLoggedEvents({ sessionId, courseId }: { sessionId: string; courseId: number }) {
		try {
			const res = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${courseId}/${sessionId}`, {
				credentials: "include",
			});
			if (res.ok) {
				const events: unknown = await res.json();
				if (this.schemaValidators.proctoringEvent.validate(events)) {
					return events;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	}

	async getVideoUrl({
		sessionId,
		recordingType,
		courseId,
	}: {
		sessionId: string;
		recordingType: string;
		courseId: string | number;
	}) {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/verity/quiz/recording/url/${courseId}/${sessionId}/${recordingType}`,
				{
					credentials: "include",
				},
			);
			if (response.ok) {
				return (await response.json()) as { [key: string]: string } | null;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	}

	async requestVideoConcatenation(sessionId: string) {
		return handleApiErrorWithSchema(
			super.fetch(`${this.config.verityOrigin}/api/verity/quiz/recording/${sessionId}/concatenated-recording`, {
				method: "POST",
				credentials: "include",
			}),
			this.schemaValidators.concatenationRequestId,
		);
	}

	async getConcatenationRequest(sessionId: string, requestId: string) {
		return handleApiErrorWithSchema(
			super.fetch(
				`${this.config.verityOrigin}/api/verity/quiz/recording/${sessionId}/concatenation-request/${requestId}`,
				{
					credentials: "include",
				},
			),
			this.schemaValidators.concatenationRequest,
		);
	}

	async deleteRecordingsFromSession(sessionUuid: string) {
		try {
			const response = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/recording/${sessionUuid}`, {
				method: "DELETE",
			});
			if (!response.ok) {
				throw new ApiClientError();
			}
			return true;
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	}

	async deleteRoomScanFromSession(sessionUuid: string) {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/verity/quiz/recording/${sessionUuid}/room_scan.webm`,
				{
					method: "DELETE",
				},
			);
			if (!response.ok) {
				throw new ApiClientError();
			}
			return true;
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	}

	async getSessionNotes({ sessionId, courseId }: { sessionId: string; courseId: number }) {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/verity/session-notes/${courseId}/${sessionId}`,
				{
					credentials: "include",
				},
			);
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.sessionNotes.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	}

	async addSessionNote({
		sessionId,
		courseId,
		content,
		timestamp,
	}: {
		sessionId: string;
		courseId: number;
		content: string;
		timestamp: Date;
	}) {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/verity/session-notes/${courseId}/${sessionId}`,
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					credentials: "include",
					body: JSON.stringify({
						content,
						timestamp,
					}),
				},
			);
			return response.ok;
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	}

	async editSessionNote({
		sessionId,
		courseId,
		noteId,
		content,
	}: {
		sessionId: string;
		courseId: number;
		noteId: number;
		content: string;
	}) {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/verity/session-notes/${courseId}/${sessionId}/${noteId}`,
				{
					method: "PATCH",
					headers: { "Content-Type": "application/json" },
					credentials: "include",
					body: JSON.stringify({
						content,
					}),
				},
			);
			return response.ok;
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	}

	async deleteSessionNote({ sessionId, courseId, noteId }: { sessionId: string; courseId: number; noteId: number }) {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/verity/session-notes/${courseId}/${sessionId}/${noteId}`,
				{
					method: "DELETE",
					credentials: "include",
				},
			);
			return response.ok;
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	}

	async getRoomScanNotes({ sessionId, courseId }: { sessionId: string; courseId: number }) {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/verity/roomscan-notes/${courseId}/${sessionId}`,
				{
					credentials: "include",
				},
			);
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.roomScanNotes.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	}

	async addRoomScanNote({
		sessionId,
		courseId,
		content,
		timestamp,
	}: {
		sessionId: string;
		courseId: number;
		content: string;
		timestamp: number;
	}) {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/verity/roomscan-notes/${courseId}/${sessionId}`,
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					credentials: "include",
					body: JSON.stringify({
						content,
						timestamp,
					}),
				},
			);
			return response.ok;
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	}

	async editRoomScanNote({
		sessionId,
		courseId,
		noteId,
		content,
	}: {
		sessionId: string;
		courseId: number;
		noteId: number;
		content: string;
	}) {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/verity/roomscan-notes/${courseId}/${sessionId}/${noteId}`,
				{
					method: "PATCH",
					headers: { "Content-Type": "application/json" },
					credentials: "include",
					body: JSON.stringify({
						content,
					}),
				},
			);
			return response.ok;
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	}

	async deleteRoomScanNote({ sessionId, courseId, noteId }: { sessionId: string; courseId: number; noteId: number }) {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/verity/roomscan-notes/${courseId}/${sessionId}/${noteId}`,
				{
					method: "DELETE",
					credentials: "include",
				},
			);
			return response.ok;
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	}

	async getOwnResultsCount(courseId: number, lmsProvidedAssessmentId: string) {
		return handleApiErrorWithSchema(
			super.fetch(`${this.config.verityOrigin}/api/verity/test-results/${courseId}/${lmsProvidedAssessmentId}/count`, {
				credentials: "include",
			}),
			this.schemaValidators.count,
		);
	}

	async get(proctoringSessionId: string) {
		return handleApiErrorWithSchema(
			super.fetch(`${this.config.verityOrigin}/api/verity/proctoring-session/${proctoringSessionId}`, {
				credentials: "include",
			}),
			this.schemaValidators.proctoringSession,
		);
	}
}
